import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import {
  NOTIFICATIONS_PAGE_SIZE,
  getNotifications,
  markAllRead as markAllReadApi,
  markNotificationRead,
  deleteNotification as deleteNotificationApi,
} from '@/services/api/notification.service'
import { useNotificationStore } from '@/stores/notification.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { Notification } from '@/types/api/notification'

export function useNotificationActions() {
  const {
    setFirstPage,
    appendPage,
    markAllRead: markAllReadStore,
    markRead: markReadStore,
    remove,
    setLoading,
  } = useNotificationStore()

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  /** Reload the first page of the bell's view. */
  const refresh = useCallback(
    async (showLoading = false) => {
      if (showLoading) {
        setLoading(true)
      }

      try {
        const data = await getNotifications()

        setFirstPage(data)

        return data
      } catch {
        toast.error('Failed to load notifications')
        throw new Error('Failed to load notifications')
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [setFirstPage, setLoading]
  )

  /** Load the next page (pages hold NOTIFICATIONS_PAGE_SIZE items). */
  const loadMore = useCallback(async () => {
    const loaded = useNotificationStore.getState().notifications.length
    setIsLoadingMore(true)
    try {
      appendPage(await getNotifications(Math.floor(loaded / NOTIFICATIONS_PAGE_SIZE)))
    } catch {
      toast.error('Failed to load more notifications')
    } finally {
      setIsLoadingMore(false)
    }
  }, [appendPage])

  /** Marks the current workspace's notifications and invites read; other workspaces are untouched. */
  const markAllAsRead = useCallback(async () => {
    // Optimistic UI update
    markAllReadStore(useWorkspaceStore.getState().currentWorkspaceId)

    try {
      await markAllReadApi()
    } catch {
      // Keep optimistic state.
      // Poller will eventually sync it with server.
    }
  }, [markAllReadStore])

  /** Marks one notification read once its link was opened; the poller corrects the count if the call fails. */
  const markRead = useCallback(
    async (notification: Notification) => {
      if (notification.read) return
      markReadStore(notification.id)
      try {
        await markNotificationRead(notification.id)
      } catch {
        // The next poll syncs the state.
      }
    },
    [markReadStore]
  )

  const dismiss = useCallback(
    async (notification: Notification) => {
      // Optimistic remove
      remove(notification.id)

      try {
        await deleteNotificationApi(notification.id)

        if (notification.type === 'WORKSPACE_INVITE') {
          toast.success('Invite declined')
        }
      } catch {
        toast.error('Failed to dismiss notification')

        // Restore authoritative state
        await refresh()
      }
    },
    [remove, refresh]
  )

  return {
    refresh,
    loadMore,
    isLoadingMore,
    markAllAsRead,
    markRead,
    dismiss,
  }
}